import React, { useState, useEffect } from 'react';
import { Redirect } from 'react-router';
import { t } from 'ttag';

// Import unified components
import {
  AuthLayout,
  AuthButton,
  AuthLink
} from '../UnifiedComponents';

// Import existing components
import assetsService from '../../services/assetsService';

interface ResetSuccessProps {
  theme?: 'light' | 'dark';
  history?: any;
}

const UnifiedResetSuccess: React.FC<ResetSuccessProps> = ({
  theme = 'light',
  history
}) => {
  // Component state
  const [shouldNavigateToLogin, setShouldNavigateToLogin] = useState(false);
  const [countdown, setCountdown] = useState(5);

  // Auto-redirect countdown
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      if (history) {
        history.push('/login');
      } else {
        setShouldNavigateToLogin(true);
      }
    }
  }, [countdown, history]);

  /**
   * Handles immediate navigation to login
   */
  const handleNavigateToLogin = () => {
    if (history) {
      history.push('/login');
    } else {
      setShouldNavigateToLogin(true);
    }
  };

  // Handle navigation redirects
  if (shouldNavigateToLogin) {
    return <Redirect to='/' />;
  }

  return (
    <AuthLayout
      theme={theme}
      title={t`Password Reset Successful!`}
      subtitle={t`Your password has been successfully reset. You can now log in with your new password.`}
      logo={assetsService.getResourcePath('logo.png')}
      onLogoError={() => {
        // Handle logo error silently
      }}
    >
      {/* Success Icon */}
      <div style={{
        textAlign: 'center',
        marginBottom: '24px',
        fontSize: '48px',
        color: '#28A745'
      }}>
        ✓
      </div>

      {/* Success Message */}
      <div style={{
        textAlign: 'center',
        marginBottom: '32px',
        padding: '16px',
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        borderRadius: '8px',
        border: '1px solid rgba(40, 167, 69, 0.3)',
        color: '#28A745'
      }}>
        <p style={{ margin: 0, fontSize: '16px' }}>
          {t`Your password has been successfully updated. You will be redirected to the login page in ${countdown} seconds.`}
        </p>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        alignItems: 'center'
      }}>
        <AuthButton
          theme={theme}
          variant="primary"
          onClick={handleNavigateToLogin}
          fullWidth
        >
          {t`Go to Login Now`}
        </AuthButton>
      </div>

      {/* Additional Information */}
      <div style={{
        textAlign: 'center',
        marginTop: '24px',
        fontSize: '14px',
        color: '#666'
      }}>
        <p>
          {t`If you didn't request this password reset, please contact support immediately.`}
        </p>
      </div>
    </AuthLayout>
  );
};

export default UnifiedResetSuccess;
